import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {ChevronDown, ChevronUp, Clock, Shield, Truck, RefreshCw, HelpCircle} from 'lucide-react';

const FAQAccordionBit = ({ activeCategory, searchQuery, isMobile = false }) => {
    const [openItems, setOpenItems] = useState([]);

    const faqItems = [
        {
            id: 1,
            category: 'shipping',
            question: "How long does shipping take?",
            answer: "Standard shipping takes 4-7 business days within the continental US. Express shipping delivers in 2-3 business days, and overnight shipping is available for next business day delivery. International shipping typically takes 7-14 business days. All orders are processed within 24 hours of placement.",
            icon: Clock,
            popular: true
        },
        {
            id: 2,
            category: 'shipping',
            question: "How do I track my order?",
            answer: "Once your order ships, you'll receive a tracking number via email. You can track your order in real-time through our tracking portal or directly with the carrier. For international orders, tracking is available once the package reaches the destination country. If you have any issues with tracking, contact our support team.",
            icon: Truck
        },
        {
            id: 3,
            category: 'ingredients',
            question: "Are KROMAIC products safe?",
            answer: "All KROMAIC products undergo rigorous safety testing and are formulated without parabens, sulfates, phthalates, synthetic fragrances, or artificial colors. We use only dermatologist-tested, non-toxic ingredients that are certified cruelty-free and vegan. Each batch is tested for purity and efficacy.",
            icon: Shield,
            popular: true
        },
        {
            id: 4,
            category: 'ingredients',
            question: "Are products dermatologically tested?",
            answer: "Yes, all KROMAIC formulations undergo clinical dermatological testing by independent laboratories. Our products are tested for skin compatibility, irritation potential, and efficacy. We also conduct patch testing and user trials to ensure safety for all skin types, including sensitive skin.",
            icon: Shield
        },
        {
            id: 5,
            category: 'returns',
            question: "Can I return or exchange products?",
            answer: "We offer a 30-day return policy for unused, unopened products in their original packaging. Opened products can be returned within 14 days for store credit. Exchanges are free within the US. To initiate a return, visit your account page or contact our support team. Returns are processed within 5-7 business days.",
            icon: RefreshCw,
            popular: true
        },
        {
            id: 6,
            category: 'products',
            question: "How should I use KROMAIC products?",
            answer: "Start with clean skin. Apply serums from thinnest to thickest consistency, allowing each product to absorb for 30 seconds. Follow with moisturizer and sunscreen during the day. For nighttime, use treatment products after cleansing. We recommend patch testing new products and introducing one new product at a time.",
            icon: Shield
        },
        {
            id: 7,
            category: 'payments',
            question: "What payment methods do you accept?",
            answer: "We accept all major credit cards (Visa, Mastercard, American Express, Discover), PayPal, Apple Pay, Google Pay, and Shop Pay. All payments are processed through secure, encrypted systems. For business/wholesale orders, we also accept wire transfers with prior arrangement.",
            icon: Shield
        },
        {
            id: 8,
            category: 'orders',
            question: "How do I modify or cancel my order?",
            answer: "Orders can be modified or cancelled within 1 hour of placement by contacting our support team. After 1 hour, orders enter processing and cannot be changed. If you need to change shipping address or items after this window, please contact us immediately—we'll do our best to accommodate your request.",
            icon: Clock
        },
        {
            id: 9,
            category: 'products',
            question: "How should I store KROMAIC products?",
            answer: "Store products in a cool, dry place away from direct sunlight and heat. Keep lids tightly closed. Refrigeration is not required but can extend the shelf life of certain active ingredients. Avoid storing in humid environments like bathrooms. Products are best used within 12 months of opening.",
            icon: Shield
        },
        {
            id: 10,
            category: 'shipping',
            question: "Do you ship internationally?",
            answer: "Yes, we ship to over 50 countries worldwide. International shipping rates and delivery times vary by destination. All customs duties and import taxes are the responsibility of the recipient. International orders may be subject to additional processing time for customs clearance.",
            icon: Truck
        }
    ];

    // Filter FAQ items based on category and search query
    const filteredItems = faqItems.filter(item => {
        if (activeCategory !== 'all' && item.category !== activeCategory) return false;
        if (searchQuery && !item.question.toLowerCase().includes(searchQuery.toLowerCase())) return false;
        return true;
    });

    const toggleItem = (id) => {
        setOpenItems(prev =>
            prev.includes(id)
                ? prev.filter(itemId => itemId !== id)
                : [...prev, id]
        );
    };

    return (
        <div className="space-y-4">
            {filteredItems.length > 0 ? (
                filteredItems.map((item, index) => (
                    <motion.div
                        key={item.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        layout
                        className={`luxury-card overflow-hidden ${
                            openItems.includes(item.id) ? 'shadow-lift' : ''
                        }`}
                    >
                        <button
                            onClick={() => toggleItem(item.id)}
                            className="w-full p-4 md:p-6 text-left flex items-center justify-between gap-4 hover:bg-nude/5 transition-colors"
                        >
                            <div className="flex items-start gap-4 flex-1">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-champagne/20 to-sage/20 flex items-center justify-center flex-shrink-0">
                                    <item.icon className="w-5 h-5 text-champagne" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <h3 className="font-medium text-charcoal text-left">
                                            {item.question}
                                        </h3>
                                        {item.popular && (
                                            <span className="text-xs px-2 py-0.5 bg-champagne/10 text-champagne rounded-full">
                        Popular
                      </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                    <span className="text-xs px-2 py-0.5 bg-nude/30 text-charcoal/50 rounded-full capitalize">
                      {item.category}
                    </span>
                                    </div>
                                </div>
                            </div>

                            <motion.div
                                animate={{ rotate: openItems.includes(item.id) ? 180 : 0 }}
                                transition={{ duration: 0.3 }}
                                className="w-8 h-8 rounded-full border border-nude flex items-center justify-center flex-shrink-0"
                            >
                                {openItems.includes(item.id) ? (
                                    <ChevronUp className="w-4 h-4 text-charcoal/50" />
                                ) : (
                                    <ChevronDown className="w-4 h-4 text-charcoal/50" />
                                )}
                            </motion.div>
                        </button>

                        <AnimatePresence>
                            {openItems.includes(item.id) && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="overflow-hidden"
                                >
                                    <div className="px-4 md:px-6 pb-4 md:pb-6 pt-2">
                                        <div className="pl-14">
                                            <p className="text-charcoal/60 leading-relaxed">
                                                {item.answer}
                                            </p>
                                            {item.related && (
                                                <div className="mt-4 pt-4 border-t border-nude/30">
                                                    <p className="text-sm font-medium text-charcoal mb-2">Related questions:</p>
                                                    <div className="flex flex-wrap gap-2">
                                                        {item.related.map((relatedQ, idx) => (
                                                            <button
                                                                key={idx}
                                                                className="text-xs px-3 py-1 bg-nude/20 rounded-full text-charcoal/60 hover:text-champagne transition-colors"
                                                            >
                                                                {relatedQ}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                ))
            ) : (
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                >
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-champagne/20 to-sage/20 flex items-center justify-center mx-auto mb-4">
                        <HelpCircle className="w-8 h-8 text-champagne" />
                    </div>
                    <h3 className="text-xl font-medium text-charcoal mb-2">
                        No questions found
                    </h3>
                    <p className="text-charcoal/60">
                        Try adjusting your search or filter to find what you're looking for.
                    </p>
                </motion.div>
            )}
        </div>
    );
};

export default FAQAccordionBit;